/**
 * osSupport — the honest device/OS support floor (testmap 20260821 #12, owner mandate:
 * "if you have requirements that it should be using this type of OS … you must provide that
 * as a notice to the end user").
 *
 * Policy: HARD-block only what genuinely cannot run (getUserMedia absent — the existing
 * `unsupported` phase). BELOW-FLOOR-but-present gets an ADVISORY notice naming the minimum —
 * the customer may still try (an old device that works is not refused), but they are told
 * plainly why a failure would not be their fault and what to update.
 *
 * The floors (conservative, evidence-based — not aspirational):
 *  - iOS < 14.3 — getUserMedia/WebRTC in Safari below 14.3 is too broken for a regulated
 *    video call (14.3 is also where camera access in non-Safari iOS browsers arrived).
 *  - Android Chrome < 90 (≈2021) — pre-90 Chrome misses fixes this call path relies on.
 *  - Samsung Internet < 14 — same era.
 *
 * Pure module (UA parse only, no side effects) so the policy is pinned by test.
 */

export const IOS_MIN_LABEL = 'iOS 14.3';
export const ANDROID_CHROME_MIN = 90;
export const SAMSUNG_MIN = 14;

export interface OsSupportNotice {
  /** Deck key for the advisory copy. */
  readonly key: string;
  /** `{min}` interpolation — the named minimum the copy states. */
  readonly min: string;
}

/** The advisory notice for this UA, or null (at/above the floor, or not recognisably below it). */
export function osSupportNotice(ua: string): OsSupportNotice | null {
  // iOS — the version rides "OS 14_2 like Mac OS X" on iPhone/iPad UAs.
  const ios = /iPhone|iPad|iPod/.test(ua) ? /OS (\d+)[._](\d+)/.exec(ua) : null;
  if (ios) {
    const major = Number(ios[1]);
    const minor = Number(ios[2]);
    if (major < 14 || (major === 14 && minor < 3)) {
      return { key: 'droom.perm.osnotice.ios', min: IOS_MIN_LABEL };
    }
    return null;
  }
  if (/Android/.test(ua)) {
    // Samsung Internet carries its own token BESIDE a Chrome token — check it first.
    const samsung = /SamsungBrowser\/(\d+)/.exec(ua);
    if (samsung) {
      return Number(samsung[1]) < SAMSUNG_MIN
        ? { key: 'droom.perm.osnotice.samsung', min: `Samsung Internet ${SAMSUNG_MIN}` }
        : null;
    }
    const chrome = /Chrome\/(\d+)/.exec(ua);
    if (chrome && Number(chrome[1]) < ANDROID_CHROME_MIN) {
      return { key: 'droom.perm.osnotice.android', min: `Chrome ${ANDROID_CHROME_MIN}` };
    }
  }
  return null;
}
